import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ExternalLink,
  Edit,
  Star,
  StarOff,
  Eye,
  EyeOff,
  MoreVertical,
  CheckCircle,
  XCircle,
  User,
  Home,
  Contact,
  Folder
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { AppSubmission } from '@/hooks/useAppSubmissions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useNostr } from '@nostrify/react';
import { useAuthor } from '@/hooks/useAuthor';
import { nip19 } from 'nostr-tools';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { generateVividGradient } from '@/lib/utils';

interface AppShowcaseCardProps {
  app: AppSubmission;
  onEdit?: (app: AppSubmission) => void;
  showModerationControls?: boolean;
  hideApprovalStatus?: boolean; // Hide pending/approval badges
}

export function AppShowcaseCard({ app, onEdit, showModerationControls, hideApprovalStatus: _hideApprovalStatus }: AppShowcaseCardProps) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const queryClient = useQueryClient();
  const [imageError, setImageError] = useState(false);
  const { toast } = useToast();

  // Get author pubkey from authorNpub field
  const authorPubkey = React.useMemo(() => {
    if (!app.authorNpub) return app.pubkey; // Fallback to event pubkey

    try {
      // If it's already a hex key, use it directly
      if (app.authorNpub.length === 64 && /^[0-9a-f]+$/i.test(app.authorNpub)) {
        return app.authorNpub;
      }

      // Otherwise try to decode as npub
      const decoded = nip19.decode(app.authorNpub);
      return decoded.type === 'npub' ? decoded.data : app.pubkey;
    } catch {
      return app.pubkey; // Fallback to event pubkey if decode fails
    }
  }, [app.authorNpub, app.pubkey]);

  const { data: authorData } = useAuthor(authorPubkey);
  const isOwner = user?.pubkey === app.pubkey;
  const MODERATOR_NPUB = 'npub1jvnpg4c6ljadf5t6ry0w9q0rnm4mksde87kglkrc993z46c39axsgq89sc';
  const MODERATOR_HEX = (() => {
    try {
      const decoded = nip19.decode(MODERATOR_NPUB);
      return decoded.type === 'npub' ? decoded.data : '';
    } catch {
      return '';
    }
  })();
  const isModerator = user?.pubkey === MODERATOR_HEX;

  // Check if this is a Halloween submission by looking for Halloween tags
  const isHalloweenSubmission = app.appTags.includes('Halloween Hackathon 2025');

  // Generate a deterministic vivid gradient based on the app id
  const gradientStyle = useMemo(() => ({
    background: generateVividGradient(app.id),
  }), [app.id]);

  // Get contact info from Halloween submissions (these have lightning/email tags)
  const getContactInfo = () => {
    if (!isHalloweenSubmission) return null;

    // Look for lightning and email tags in the event tags
    const lightningTag = app.tags?.find((tag: string[]) => tag[0] === 'lightning')?.[1];
    const emailTag = app.tags?.find((tag: string[]) => tag[0] === 'email')?.[1];

    return { lightning: lightningTag, email: emailTag };
  };

  const handleCopyContactInfo = () => {
    const contactInfo = getContactInfo();
    if (!contactInfo) return;

    const info: string[] = [];
    if (contactInfo.lightning) info.push(`Lightning: ${contactInfo.lightning}`);
    if (contactInfo.email) info.push(`Email: ${contactInfo.email}`);

    const textToCopy = info.join('\n');
    navigator.clipboard.writeText(textToCopy);

    toast({
      title: 'Contact Info Copied!',
      description: `Copied ${info.length} contact method(s) to clipboard`
    });
  };

  const handleModerationAction = async (action: 'feature' | 'unfeature' | 'approve' | 'unapprove' | 'hide' | 'unhide' | 'homepage' | 'unhomepage') => {
    if (!isModerator || !user) return;

    const dTag = app.tags.find(tag => tag[0] === 'd')?.[1];
    if (!dTag) {
      toast({
        title: 'Error',
        description: 'Cannot perform moderation action: missing app identifier',
        variant: 'destructive'
      });
      return;
    }

    const appCoordinate = `${app.kind}:${app.pubkey}:${dTag}`;

    try {
      if (action === 'homepage' || action === 'unhomepage') {
        // Get current homepage apps list
        const currentHomepageLists = await nostr.query([{
          kinds: [30267],
          authors: [MODERATOR_HEX],
          '#d': ['soapbox-homepage-apps'],
          limit: 1
        }]);

        const currentHomepageList = currentHomepageLists.reduce((latest: NostrEvent | null, current: NostrEvent) =>
          !latest || current.created_at > latest.created_at ? current : latest,
          null as NostrEvent | null
        );

        // Get current homepage app coordinates
        const currentHomepageApps = new Set<string>();
        if (currentHomepageList) {
          currentHomepageList.tags
            .filter(tag => tag[0] === 'a')
            .forEach(tag => currentHomepageApps.add(tag[1]));
        }

        // Update the homepage set
        if (action === 'homepage') {
          currentHomepageApps.add(appCoordinate);
        } else {
          currentHomepageApps.delete(appCoordinate);
        }

        // Publish updated homepage apps list
        publishEvent({
          kind: 30267,
          content: 'Soapbox homepage apps curated by moderators',
          tags: [
            ['d', 'soapbox-homepage-apps'],
            ['title', 'Soapbox Homepage Apps'],
            ['description', 'Apps displayed on the Soapbox homepage'],
            ...Array.from(currentHomepageApps).map(coord => ['a', coord])
          ],
          created_at: Math.floor(Date.now() / 1000)
        });

        // If adding to homepage, also ensure it's approved
        if (action === 'homepage') {
          // Get current approved apps list
          const currentApprovedLists = await nostr.query([{
            kinds: [30267],
            authors: [MODERATOR_HEX],
            '#d': ['soapbox-approved-apps'],
            limit: 1
          }]);

          const currentApprovedList = currentApprovedLists.reduce((latest: NostrEvent | null, current: NostrEvent) =>
            !latest || current.created_at > latest.created_at ? current : latest,
            null as NostrEvent | null
          );

          // Get current approved app coordinates
          const currentApprovedApps = new Set<string>();
          if (currentApprovedList) {
            currentApprovedList.tags
              .filter(tag => tag[0] === 'a')
              .forEach(tag => currentApprovedApps.add(tag[1]));
          }

          // Add to approved list if not already there
          currentApprovedApps.add(appCoordinate);

          // Publish updated approved apps list
          publishEvent({
            kind: 30267,
            content: 'Soapbox approved apps curated by moderators',
            tags: [
              ['d', 'soapbox-approved-apps'],
              ['title', 'Soapbox Approved Apps'],
              ['description', 'Apps approved for the Soapbox showcase'],
              ...Array.from(currentApprovedApps).map(coord => ['a', coord])
            ],
            created_at: Math.floor(Date.now() / 1000)
          });
        }

      } else if (action === 'feature' || action === 'unfeature') {
        // Get current featured apps list
        const currentFeaturedLists = await nostr.query([{
          kinds: [30267],
          authors: [MODERATOR_HEX],
          '#d': ['soapbox-featured-apps'],
          limit: 1
        }]);

        const currentFeaturedList = currentFeaturedLists.reduce((latest: NostrEvent | null, current: NostrEvent) =>
          !latest || current.created_at > latest.created_at ? current : latest,
          null as NostrEvent | null
        );

        // Get current featured app coordinates
        const currentFeaturedApps = new Set<string>();
        if (currentFeaturedList) {
          currentFeaturedList.tags
            .filter(tag => tag[0] === 'a')
            .forEach(tag => currentFeaturedApps.add(tag[1]));
        }

        // Update the featured set
        if (action === 'feature') {
          currentFeaturedApps.add(appCoordinate);
        } else {
          currentFeaturedApps.delete(appCoordinate);
        }

        // Publish updated featured apps list
        publishEvent({
          kind: 30267,
          content: 'Soapbox featured apps curated by moderators',
          tags: [
            ['d', 'soapbox-featured-apps'],
            ['title', 'Soapbox Featured Apps'],
            ['description', 'Apps featured in the Soapbox showcase'],
            ...Array.from(currentFeaturedApps).map(coord => ['a', coord])
          ],
          created_at: Math.floor(Date.now() / 1000)
        });

        // If featuring an app, also ensure it's approved
        if (action === 'feature') {
          // Get current approved apps list
          const currentApprovedLists = await nostr.query([{
            kinds: [30267],
            authors: [MODERATOR_HEX],
            '#d': ['soapbox-approved-apps'],
            limit: 1
          }]);

          const currentApprovedList = currentApprovedLists.reduce((latest: NostrEvent | null, current: NostrEvent) =>
            !latest || current.created_at > latest.created_at ? current : latest,
            null as NostrEvent | null
          );

          // Get current approved app coordinates
          const currentApprovedApps = new Set<string>();
          if (currentApprovedList) {
            currentApprovedList.tags
              .filter(tag => tag[0] === 'a')
              .forEach(tag => currentApprovedApps.add(tag[1]));
          }

          // Add to approved list if not already there
          currentApprovedApps.add(appCoordinate);

          // Publish updated approved apps list
          publishEvent({
            kind: 30267,
            content: 'Soapbox approved apps curated by moderators',
            tags: [
              ['d', 'soapbox-approved-apps'],
              ['title', 'Soapbox Approved Apps'],
              ['description', 'Apps approved for the Soapbox showcase'],
              ...Array.from(currentApprovedApps).map(coord => ['a', coord])
            ],
            created_at: Math.floor(Date.now() / 1000)
          });
        }

      } else if (action === 'approve' || action === 'unapprove') {
        // Get current approved apps list
        const currentApprovedLists = await nostr.query([{
          kinds: [30267],
          authors: [MODERATOR_HEX],
          '#d': ['soapbox-approved-apps'],
          limit: 1
        }]);

        const currentApprovedList = currentApprovedLists.reduce((latest: NostrEvent | null, current: NostrEvent) =>
          !latest || current.created_at > latest.created_at ? current : latest,
          null as NostrEvent | null
        );

        // Get current approved app coordinates
        const currentApprovedApps = new Set<string>();
        if (currentApprovedList) {
          currentApprovedList.tags
            .filter(tag => tag[0] === 'a')
            .forEach(tag => currentApprovedApps.add(tag[1]));
        }

        // Update the approved set
        if (action === 'approve') {
          currentApprovedApps.add(appCoordinate);
        } else {
          currentApprovedApps.delete(appCoordinate);
        }

        // Publish updated approved apps list
        publishEvent({
          kind: 30267,
          content: 'Soapbox approved apps curated by moderators',
          tags: [
            ['d', 'soapbox-approved-apps'],
            ['title', 'Soapbox Approved Apps'],
            ['description', 'Apps approved for the Soapbox showcase'],
            ...Array.from(currentApprovedApps).map(coord => ['a', coord])
          ],
          created_at: Math.floor(Date.now() / 1000)
        });

      } else if (action === 'hide' || action === 'unhide') {
        // Create NIP-56 report event
        publishEvent({
          kind: 1984,
          content: `App ${action}den by moderator: "${app.appName}"`,
          tags: [
            ['a', appCoordinate, action === 'hide' ? 'spam' : 'other'],
            ['p', app.pubkey],
            ['L', 'soapbox.moderation'],
            ['l', action === 'hide' ? 'hidden' : 'unhidden', 'soapbox.moderation']
          ],
          created_at: Math.floor(Date.now() / 1000)
        });
      }

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['nostr', 'app-submissions'] });

      // If this is a Halloween app, also invalidate Halloween queries
      if (isHalloweenSubmission) {
        queryClient.invalidateQueries({ queryKey: ['nostr', 'halloween-submissions'] });
        queryClient.invalidateQueries({ queryKey: ['nostr', 'halloween-submissions-with-contacts'] });
      }

      toast({
        title: 'Success!',
        description: `App "${app.appName}" ${action}d successfully`
      });

    } catch (error) {
      console.error('Moderation action failed:', error);
      toast({
        title: 'Error',
        description: `Failed to ${action} app "${app.appName}"`,
        variant: 'destructive'
      });
    }
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 hover:border-primary/30 hover:-translate-y-1 h-full flex flex-col border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <div className="relative">
        {/* App Header with Cover Image or Gradient */}
        {app.coverImageUrl ? (
          <a
            href={app.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative block aspect-[16/10] overflow-hidden cursor-pointer"
          >
            <img
              src={app.coverImageUrl}
              alt={`${app.appName} cover`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to gradient if image fails to load
                const target = e.target as HTMLImageElement;
                const parent = target.parentElement;
                if (parent) {
                  parent.style.cssText = `background: ${generateVividGradient(app.id)}`;
                  parent.classList.add('flex', 'items-center', 'justify-center');
                  parent.innerHTML = '<svg class="h-16 w-16 text-white/50" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>';
                }
              }}
            />
          </a>
        ) : (
          <a
            href={app.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="relative block aspect-[16/10] flex items-center justify-center overflow-hidden cursor-pointer"
            style={gradientStyle}
          >
            <Folder className="h-16 w-16 text-white/50" />
          </a>
        )}

        {/* Actions Menu */}
        {(isOwner || showModerationControls) && (
          <div className="absolute top-3 right-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isOwner && (
                  <DropdownMenuItem onClick={() => onEdit?.(app)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit App
                  </DropdownMenuItem>
                )}
                {showModerationControls && isModerator && (
                  <>
                    {isHalloweenSubmission && getContactInfo() && (getContactInfo()?.lightning || getContactInfo()?.email) && (
                      <DropdownMenuItem
                        onClick={handleCopyContactInfo}
                      >
                        <Contact className="w-4 h-4 mr-2" />
                        View Contact Info
                      </DropdownMenuItem>
                    )}
                    {!app.isApproved && (
                      <DropdownMenuItem
                        onClick={() => handleModerationAction('approve')}
                        disabled={isPending}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </DropdownMenuItem>
                    )}
                    {app.isApproved && !app.isFeatured && (
                      <DropdownMenuItem
                        onClick={() => handleModerationAction('unapprove')}
                        disabled={isPending}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Unapprove
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleModerationAction(app.isFeatured ? 'unfeature' : 'feature')}
                      disabled={isPending}
                    >
                      {app.isFeatured ? (
                        <>
                          <StarOff className="w-4 h-4 mr-2" />
                          Un-feature
                        </>
                      ) : (
                        <>
                          <Star className="w-4 h-4 mr-2" />
                          Feature
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleModerationAction(app.isHomepage ? 'unhomepage' : 'homepage')}
                      disabled={isPending}
                    >
                      {app.isHomepage ? (
                        <>
                          <Home className="w-4 h-4 mr-2" />
                          Remove from Homepage
                        </>
                      ) : (
                        <>
                          <Home className="w-4 h-4 mr-2" />
                          Add to Homepage
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleModerationAction(app.isHidden ? 'unhide' : 'hide')}
                      disabled={isPending}
                    >
                      {app.isHidden ? (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Unhide
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          Hide
                        </>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <CardContent className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* App Icon */}
            <div className="w-11 h-11 rounded-xl overflow-hidden bg-gradient-to-br from-muted to-muted/50 flex-shrink-0 shadow-sm ring-1 ring-border/50">
              <img
                src={app.appIconUrl}
                alt={`${app.appName} icon`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground leading-tight" title={app.appName}>
                {app.appName.length > 50 ? `${app.appName.slice(0, 50)}...` : app.appName}
              </h3>
            </div>
          </div>
        </div>

        <p className="text-muted-foreground text-sm mb-3 line-clamp-2 leading-relaxed">{app.description}</p>

        {/* Author Information */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 rounded-full overflow-hidden bg-muted flex-shrink-0 ring-1 ring-border/30">
            {authorData?.metadata?.picture ? (
              <img
                src={authorData.metadata.picture}
                alt={`${authorData.metadata.name || 'Author'} avatar`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-2.5 h-2.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path></svg></div>';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-2.5 h-2.5 text-muted-foreground" />
              </div>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            by{' '}
            <a
              href={`https://ditto.pub/${app.authorNpub || nip19.npubEncode(authorPubkey)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/80 hover:text-primary transition-colors hover:underline font-medium"
            >
              {authorData?.metadata?.name || authorData?.metadata?.display_name || 'Anonymous'}
            </a>
          </span>
        </div>

        {/* Spacer to push action links to bottom */}
        <div className="flex-1"></div>

        {/* Action Links */}
        <div className="flex items-center gap-3 mt-auto">
          <Link
            to={`/clone?url=${encodeURIComponent(app.repositoryUrl)}`}
            className="flex-1"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-900 dark:bg-zinc-800 hover:bg-zinc-800 dark:hover:bg-zinc-700 border border-zinc-700 rounded-lg transition-colors text-sm">
              <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M4 9h12a2 2 0 0 1 2 2v6a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4v-6a2 2 0 0 1 2-2z"/>
                <path d="M18 11h1a2 2 0 0 1 0 4h-1"/>
                <path d="M6 5v2"/>
                <path d="M10 5v2"/>
                <path d="M14 5v2"/>
              </svg>
              <span className="text-zinc-400">Edit with</span>
              <span className="text-zinc-100 font-semibold">AfterHours</span>
            </div>
          </Link>
          <Button variant="outline" size="sm" className="h-9 w-9 p-0 rounded-lg border-border/50 hover:bg-muted/50" asChild>
            <a
              href={app.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Visit app"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}